/**
 * Página de compartilhamento do fluxo
 * Gerencia links públicos, slug e embed code
 * URL pública: /chat/{tenant_slug}/{flow_slug}
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Copy,
  Check,
  Link2,
  Globe,
  Edit2,
  ExternalLink,
  AlertCircle,
  Monitor,
  MessageSquareMore,
  MessageCircle,
  ChevronLeft,
  Eye,
  Code2,
  Palette,
  Settings2,
  AlignLeft,
  AlignRight,
  X,
  BarChart3,
  Info,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { MediaUrlInput } from '@/components/ui/media-url-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FlowBuilderTabs } from '@/components/tenant/flows/FlowBuilderTabs';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { showToast } from '@/lib/toast';
import { logger } from '@/lib/logger';

// URL base do app
const APP_BASE_URL = 'https://app.uaizychat.com.br';

// ============================================================================
// Tipos e configurações de embed
// ============================================================================

/** Modos de incorporação disponíveis */
type EmbedMode = 'standard' | 'popup' | 'bubble' | null;

/** Configurações do modo Standard (iframe) */
interface StandardConfig {
  fullscreen: boolean;
  width: string;
  widthUnit: '%' | 'px';
  height: string;
  heightUnit: 'px';
}

/** Configurações do modo Popup (modal overlay) */
interface PopupConfig {
  autoShow: boolean;
  delaySeconds: number;
}

/** Configurações do modo Bubble (bolha flutuante) */
interface BubbleConfig {
  showPreview: boolean;
  previewMessage: string;
  avatarUrl: string;
  autoShow: boolean;
  autoShowDelay: number;
  themeColor: string;
  placement: 'right' | 'left';
  buttonSize: 'small' | 'medium' | 'large';
  previewBgColor: string;
  previewTextColor: string;
  closeBtnBgColor: string;
  closeBtnIconColor: string;
  /** URL customizada do ícone do botão bubble (quando vazio, usa emoji 💬) */
  buttonIconUrl: string;
}

/** Valores padrão para cada modo */
const DEFAULT_STANDARD: StandardConfig = {
  fullscreen: false,
  width: '100',
  widthUnit: '%',
  height: '600',
  heightUnit: 'px',
};

const DEFAULT_POPUP: PopupConfig = {
  autoShow: false,
  delaySeconds: 3,
};

const DEFAULT_BUBBLE: BubbleConfig = {
  showPreview: true,
  previewMessage: 'Olá! Como posso ajudar?',
  avatarUrl: '',
  autoShow: false,
  autoShowDelay: 3,
  themeColor: '#0ea5e9',
  placement: 'right',
  buttonSize: 'medium',
  previewBgColor: '#ffffff',
  previewTextColor: '#333333',
  closeBtnBgColor: '#00000026',
  closeBtnIconColor: '#333333',
  buttonIconUrl: '',
};

/** Dimensões por tamanho do botão bubble */
const BUBBLE_BUTTON_SIZES = {
  small: { size: 48, icon: 22 },
  medium: { size: 60, icon: 28 },
  large: { size: 72, icon: 34 },
};

// ============================================================================
// Gerador de código de incorporação
// ============================================================================

/**
 * Gera o snippet HTML/JS para incorporar o chat no site do usuário
 * @param mode - Modo de incorporação selecionado
 * @param config - Configurações específicas do modo
 * @param url - URL pública do fluxo
 * @returns Código HTML/JS pronto para copiar
 */
function generateEmbedCode(
  mode: EmbedMode,
  config: StandardConfig | PopupConfig | BubbleConfig,
  url: string
): string {
  switch (mode) {
    case 'standard': {
      const c = config as StandardConfig;
      if (c.fullscreen) {
        return `<iframe
  src="${url}"
  style="position: fixed; inset: 0; width: 100%; height: 100%; border: none; z-index: 9999;"
  allow="microphone"
></iframe>`;
      }
      return `<iframe
  src="${url}"
  style="width: ${c.width}${c.widthUnit}; height: ${c.height}${c.heightUnit}; border: none;"
  allow="microphone"
></iframe>`;
    }

    case 'popup': {
      const c = config as PopupConfig;
      return `<script>
(function() {
  // Configuração do Popup UaizyChat
  var CHAT_URL = "${url}";
  var AUTO_SHOW = ${c.autoShow};
  var DELAY = ${c.delaySeconds * 1000};

  // Estilos do overlay e botão
  var style = document.createElement('style');
  style.textContent = \`
    .uaizy-popup-overlay {
      display: none; position: fixed; inset: 0; z-index: 99999;
      background: rgba(0,0,0,0.5); align-items: center; justify-content: center;
    }
    .uaizy-popup-overlay.active { display: flex; }
    .uaizy-popup-frame {
      width: 90vw; max-width: 800px; height: 80vh;
      border-radius: 12px; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,0.25);
      position: relative; background: #fff;
    }
    .uaizy-popup-close {
      position: absolute; top: 8px; right: 8px; z-index: 1;
      width: 32px; height: 32px; border-radius: 50%; border: none;
      background: rgba(0,0,0,0.1); color: #333; font-size: 18px;
      cursor: pointer; display: flex; align-items: center; justify-content: center;
    }
    .uaizy-popup-close:hover { background: rgba(0,0,0,0.2); }
    .uaizy-popup-btn {
      position: fixed; bottom: 24px; right: 24px; z-index: 99998;
      width: 56px; height: 56px; border-radius: 50%; border: none;
      background: #0ea5e9; color: #fff; font-size: 24px;
      cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      display: flex; align-items: center; justify-content: center;
    }
    .uaizy-popup-btn:hover { transform: scale(1.05); }
  \`;
  document.head.appendChild(style);

  // Cria overlay
  var overlay = document.createElement('div');
  overlay.className = 'uaizy-popup-overlay';
  overlay.innerHTML = '<div class="uaizy-popup-frame">' +
    '<button class="uaizy-popup-close" onclick="this.closest(\\'.uaizy-popup-overlay\\').classList.remove(\\'active\\')">&times;</button>' +
    '<iframe src="' + CHAT_URL + '" style="width:100%;height:100%;border:none;" allow="microphone"></iframe>' +
    '</div>';
  overlay.addEventListener('click', function(e) {
    if (e.target === overlay) overlay.classList.remove('active');
  });
  document.body.appendChild(overlay);

  // Cria botão flutuante
  var btn = document.createElement('button');
  btn.className = 'uaizy-popup-btn';
  btn.innerHTML = '💬';
  btn.onclick = function() { overlay.classList.add('active'); };
  document.body.appendChild(btn);

  // Auto show com delay
  if (AUTO_SHOW) {
    setTimeout(function() { overlay.classList.add('active'); }, DELAY);
  }
})();
</script>`;
    }

    case 'bubble': {
      const c = config as BubbleConfig;
      const btnDims = BUBBLE_BUTTON_SIZES[c.buttonSize];
      const positionSide = c.placement === 'left' ? 'left' : 'right';
      const panelPosition = c.placement === 'left' ? 'left: 24px;' : 'right: 24px;';
      const previewPosition = c.placement === 'left' ? 'left: 24px;' : 'right: 24px;';
      const arrowSide = c.placement === 'left' ? 'left: 28px;' : 'right: 28px;';
      const previewBottom = btnDims.size + 16;
      const panelBottom = btnDims.size + 20;

      // Avatar HTML na preview message
      const avatarHtml = c.avatarUrl
        ? `'<img src="${c.avatarUrl}" style="width:28px;height:28px;border-radius:50%;margin-right:8px;flex-shrink:0;" />' +`
        : '';

      return `<script>
(function() {
  // Configuração da Bolha UaizyChat
  var CHAT_URL = "${url}";
  var THEME_COLOR = "${c.themeColor}";
  var SHOW_PREVIEW = ${c.showPreview};
  var PREVIEW_MSG = "${c.previewMessage.replace(/"/g, '\\"')}";
  var AUTO_SHOW = ${c.autoShow};
  var AUTO_DELAY = ${c.autoShowDelay * 1000};

  var style = document.createElement('style');
  style.textContent = \`
    .uaizy-bubble-btn {
      position: fixed; bottom: 24px; ${positionSide}: 24px; z-index: 99998;
      width: ${btnDims.size}px; height: ${btnDims.size}px; border-radius: 50%; border: none;
      background: \${THEME_COLOR}; color: #fff; font-size: ${btnDims.icon}px;
      cursor: pointer; box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.2s;
    }
    .uaizy-bubble-btn:hover { transform: scale(1.1); }
    .uaizy-bubble-preview {
      position: fixed; bottom: ${previewBottom}px; ${previewPosition} z-index: 99997;
      background: ${c.previewBgColor}; padding: 12px 16px; border-radius: 12px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.12); max-width: 260px;
      font-family: system-ui, sans-serif; font-size: 14px; color: ${c.previewTextColor};
      animation: uaizyFadeIn 0.3s ease;
      display: flex; align-items: center;
    }
    .uaizy-bubble-preview-close {
      position: absolute; top: -8px; right: -8px;
      width: 20px; height: 20px; border-radius: 50%; border: none;
      background: ${c.closeBtnBgColor}; color: ${c.closeBtnIconColor};
      font-size: 12px; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
    }
    .uaizy-bubble-preview::after {
      content: ''; position: absolute; bottom: -6px; ${arrowSide}
      width: 12px; height: 12px; background: ${c.previewBgColor};
      transform: rotate(45deg); box-shadow: 2px 2px 4px rgba(0,0,0,0.05);
    }
    .uaizy-bubble-panel {
      display: none; position: fixed; bottom: ${panelBottom}px; ${panelPosition} z-index: 99999;
      width: 400px; height: 600px; max-height: 80vh;
      border-radius: 16px; overflow: hidden;
      box-shadow: 0 8px 32px rgba(0,0,0,0.2);
      animation: uaizySlideUp 0.3s ease;
    }
    .uaizy-bubble-panel.active { display: block; }
    @keyframes uaizyFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
    @keyframes uaizySlideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
    @media (max-width: 480px) {
      .uaizy-bubble-panel { width: calc(100vw - 16px); ${positionSide}: 8px; bottom: 8px; height: calc(100vh - 16px); max-height: none; border-radius: 12px; }
    }
  \`;
  document.head.appendChild(style);

  // Cria painel de chat
  var panel = document.createElement('div');
  panel.className = 'uaizy-bubble-panel';
  panel.innerHTML = '<iframe src="' + CHAT_URL + '" style="width:100%;height:100%;border:none;" allow="microphone"></iframe>';
  document.body.appendChild(panel);

  // Cria preview de mensagem
  var preview = null;
  if (SHOW_PREVIEW) {
    preview = document.createElement('div');
    preview.className = 'uaizy-bubble-preview';
    preview.innerHTML = ${avatarHtml}
      '<span>' + PREVIEW_MSG + '</span>' +
      '<button class="uaizy-bubble-preview-close" onclick="this.parentElement.style.display=\\'none\\'">&times;</button>';
    preview.style.cursor = 'pointer';
    preview.onclick = function(e) { if (!e.target.classList.contains('uaizy-bubble-preview-close')) toggleChat(); };
    document.body.appendChild(preview);
  }

  // Cria botão bolha
  var isOpen = false;
  var btn = document.createElement('button');
  btn.className = 'uaizy-bubble-btn';
  var ICON_URL = '${c.buttonIconUrl || ''}';
  var defaultIcon = ICON_URL ? '<img src="' + ICON_URL + '" style="width:60%;height:60%;border-radius:50%;object-fit:cover;" />' : '💬';
  btn.innerHTML = defaultIcon;
  btn.onclick = function() { toggleChat(); };
  document.body.appendChild(btn);

  function toggleChat() {
    isOpen = !isOpen;
    panel.classList.toggle('active', isOpen);
    btn.innerHTML = isOpen ? '✕' : defaultIcon;
    if (preview && isOpen) { preview.style.display = 'none'; }
  }

  // Auto show com delay
  if (AUTO_SHOW && SHOW_PREVIEW && preview) {
    preview.style.display = 'none';
    setTimeout(function() { preview.style.display = 'flex'; }, AUTO_DELAY);
  }
})();
</script>`;
    }

    default:
      return '';
  }
}

// ============================================================================
// Cores disponíveis para a bolha
// ============================================================================
const BUBBLE_COLORS = [
  { label: 'Azul', value: '#0ea5e9' },
  { label: 'Verde', value: '#22c55e' },
  { label: 'Roxo', value: '#8b5cf6' },
  { label: 'Rosa', value: '#ec4899' },
  { label: 'Laranja', value: '#f97316' },
  { label: 'Vermelho', value: '#ef4444' },
];

/**
 * Página de compartilhamento
 */
export default function FlowShare() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [localSlug, setLocalSlug] = useState('');
  const [copied, setCopied] = useState(false);

  // Estados do embed
  const [embedMode, setEmbedMode] = useState<EmbedMode>(null);
  const [standardConfig, setStandardConfig] = useState<StandardConfig>(DEFAULT_STANDARD);
  const [popupConfig, setPopupConfig] = useState<PopupConfig>(DEFAULT_POPUP);
  const [bubbleConfig, setBubbleConfig] = useState<BubbleConfig>(DEFAULT_BUBBLE);
  const [embedCopied, setEmbedCopied] = useState(false);

  // Estados de rastreamento (tracking)
  const [trackingGtmId, setTrackingGtmId] = useState('');
  const [trackingPixelId, setTrackingPixelId] = useState('');
  const [trackingGa4Id, setTrackingGa4Id] = useState('');
  const [trackingCustomHead, setTrackingCustomHead] = useState('');
  const [trackingLoaded, setTrackingLoaded] = useState(false);

  // Busca dados do fluxo incluindo tenant_slug para URL hierárquica
  const { data: flow, isLoading } = useQuery({
    queryKey: ['flow-share', flowId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flows')
        .select(`
          id, 
          name, 
          status, 
          public_slug, 
          is_public, 
          public_views_count,
          gtm_id,
          meta_pixel_id,
          ga4_measurement_id,
          custom_head_code,
          tenants!inner(slug)
        `)
        .eq('id', flowId)
        .single();

      if (error) throw error;
      
      // Extrai tenant_slug do join
      return {
        ...data,
        tenant_slug: (data.tenants as unknown as { slug: string })?.slug
      };
    },
    enabled: !!flowId,
  });

  // Mutation para atualizar slug
  const updateSlugMutation = useMutation({
    mutationFn: async (newSlug: string) => {
      const { error } = await supabase
        .from('flows')
        .update({ public_slug: newSlug })
        .eq('id', flowId);

      if (error) {
        if (error.code === '23505') {
          throw new Error('Este slug já está em uso. Escolha outro.');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow-share', flowId] });
      setIsEditingSlug(false);
      showToast.success('Slug atualizado com sucesso');
    },
    onError: (error: Error) => {
      showToast.error(error.message);
    },
  });

  // Mutation para toggle público
  const togglePublicMutation = useMutation({
    mutationFn: async (isPublic: boolean) => {
      // Se ativando e não tem slug, gera um
      // Tipo seguro do Supabase para update de fluxos
      const updates: Database['public']['Tables']['flows']['Update'] = { is_public: isPublic };
      
      if (isPublic && !flow?.public_slug) {
        // Gera slug baseado no nome do fluxo
        const baseSlug = flow?.name
          ?.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') || `fluxo-${Date.now()}`;
        
        updates.public_slug = baseSlug;
      }

      const { error } = await supabase
        .from('flows')
        .update(updates)
        .eq('id', flowId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow-share', flowId] });
      showToast.success(
        flow?.is_public ? 'Link público desativado' : 'Link público ativado'
      );
    },
    onError: () => {
      showToast.error('Erro ao atualizar configuração');
    },
  });

  // Sincroniza estados de tracking quando flow carrega
  const syncTrackingState = useCallback(() => {
    if (flow && !trackingLoaded) {
      setTrackingGtmId(flow.gtm_id || '');
      setTrackingPixelId(flow.meta_pixel_id || '');
      setTrackingGa4Id(flow.ga4_measurement_id || '');
      setTrackingCustomHead(flow.custom_head_code || '');
      setTrackingLoaded(true);
    }
  }, [flow, trackingLoaded]);
  syncTrackingState();

  // Mutation para salvar configurações de rastreamento
  const saveTrackingMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('flows')
        .update({
          gtm_id: trackingGtmId.trim() || null,
          meta_pixel_id: trackingPixelId.trim() || null,
          ga4_measurement_id: trackingGa4Id.trim() || null,
          custom_head_code: trackingCustomHead.trim() || null,
        })
        .eq('id', flowId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow-share', flowId] });
      showToast.success('Configurações de rastreamento salvas');
    },
    onError: () => {
      showToast.error('Erro ao salvar configurações de rastreamento');
    },
  });

  const handleSaveSlug = () => {
    if (!localSlug.trim()) {
      showToast.error('Slug não pode estar vazio');
      return;
    }

    // Valida formato do slug
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(localSlug)) {
      showToast.error('Slug deve conter apenas letras minúsculas, números e hífens');
      return;
    }

    updateSlugMutation.mutate(localSlug);
  };

  // Handler para copiar link (URL hierárquica com tenant_slug)
  const handleCopyLink = async () => {
    const url = `${APP_BASE_URL}/chat/${flow?.tenant_slug}/${flow?.public_slug}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    showToast.success('Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Handler para copiar código de embed
  const handleCopyEmbedCode = async () => {
    const config = embedMode === 'standard' ? standardConfig
      : embedMode === 'popup' ? popupConfig
      : bubbleConfig;
    const code = generateEmbedCode(embedMode, config, publicUrl);
    await navigator.clipboard.writeText(code);
    setEmbedCopied(true);
    showToast.success('Código copiado!');
    setTimeout(() => setEmbedCopied(false), 2000);
  };

  // Handler para iniciar edição do slug
  const startEditingSlug = () => {
    setLocalSlug(flow?.public_slug || '');
    setIsEditingSlug(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // URL pública hierárquica: /chat/{tenant_slug}/{flow_slug}
  const publicUrl = `${APP_BASE_URL}/chat/${flow?.tenant_slug}/${flow?.public_slug}`;
  const isFlowActive = flow?.status === 'active';
  const isPublic = flow?.is_public || false;

  // Obtém configuração e código do embed atual
  const currentEmbedConfig = embedMode === 'standard' ? standardConfig
    : embedMode === 'popup' ? popupConfig
    : bubbleConfig;
  const embedCode = embedMode ? generateEmbedCode(embedMode, currentEmbedConfig, publicUrl) : '';

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="h-14 border-b border-border bg-card relative flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/tenant/flows')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold">{flow?.name || 'Fluxo'}</h2>
          <Badge variant={isFlowActive ? 'default' : 'secondary'}>
            {isFlowActive ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>

        {/* Tabs de navegação - posicionamento absoluto central */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <FlowBuilderTabs />
        </div>

        <div /> {/* Espaçador para manter justify-between */}
      </div>

      {/* Conteúdo principal — redesign premium */}
      <div className="flex-1 overflow-auto p-8">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* ================================================================ */}
          {/* Banner premium — fluxo inativo */}
          {/* ================================================================ */}
          {!isFlowActive && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-destructive/5 border border-destructive/20">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-destructive/10">
                <AlertCircle className="w-4 h-4 text-destructive" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">Fluxo inativo</p>
                <p className="text-xs text-muted-foreground">
                  Publique o fluxo na aba "Flow" para que o link público funcione.
                </p>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* Seção — Link Público (premium) */}
          {/* ================================================================ */}
          <div className="bg-muted/30 border border-border/40 rounded-xl p-5 space-y-4">
            {/* Header da seção */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/10">
                  <Globe className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Link Público</h3>
                  <p className="text-xs text-muted-foreground">
                    Compartilhe para qualquer pessoa acessar o chat
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {flow?.is_public ? 'Ativo' : 'Inativo'}
                </span>
                <Switch
                  checked={flow?.is_public || false}
                  onCheckedChange={(v) => togglePublicMutation.mutate(v)}
                  disabled={togglePublicMutation.isPending}
                />
              </div>
            </div>

            {flow?.is_public && flow?.public_slug ? (
              <>
                {/* Campo de URL estilizado */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">URL do Chat</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center bg-background border border-border/60 rounded-lg px-3 py-2.5 text-sm">
                      <Link2 className="h-3.5 w-3.5 mr-2 text-muted-foreground flex-shrink-0" />
                      <span className="text-muted-foreground truncate">
                        {APP_BASE_URL}/chat/{flow?.tenant_slug}/
                      </span>
                      {isEditingSlug ? (
                        <Input
                          value={localSlug}
                          onChange={(e) => setLocalSlug(e.target.value.toLowerCase())}
                          className="h-6 px-1 py-0 w-40 ml-0 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveSlug();
                            if (e.key === 'Escape') setIsEditingSlug(false);
                          }}
                        />
                      ) : (
                        <span className="font-semibold text-foreground">{flow.public_slug}</span>
                      )}
                    </div>

                    {/* Botões de ação com tooltips */}
                    <TooltipProvider delayDuration={200}>
                      {isEditingSlug ? (
                        <>
                          <Button size="sm" onClick={handleSaveSlug} disabled={updateSlugMutation.isPending}>
                            Salvar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setIsEditingSlug(false)}>
                            Cancelar
                          </Button>
                        </>
                      ) : (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon-sm" variant="outline" onClick={startEditingSlug}>
                                <Edit2 className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Editar slug</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon-sm" variant="outline" onClick={handleCopyLink}>
                                {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{copied ? 'Copiado!' : 'Copiar link'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon-sm" variant="outline" onClick={() => window.open(publicUrl, '_blank')}>
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Abrir em nova aba</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                    </TooltipProvider>
                  </div>
                </div>

                {/* Badge de visualizações premium */}
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center gap-1.5 bg-background border border-border/50 rounded-full px-3 py-1">
                    <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">
                      {flow.public_views_count || 0}
                    </span>
                    <span className="text-xs text-muted-foreground">visualizações</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-2">
                Ative o link público para gerar uma URL compartilhável.
              </p>
            )}
          </div>

          {/* ================================================================ */}
          {/* Seção — Código de Incorporação (premium) */}
          {/* ================================================================ */}
          <div className={`bg-muted/30 border border-border/40 rounded-xl p-5 space-y-4 transition-opacity ${!isPublic ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Header da seção */}
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-violet-500/10">
                <Code2 className="w-4 h-4 text-violet-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Código de Incorporação</h3>
                <p className="text-xs text-muted-foreground">
                  Incorpore o chat em qualquer site ou aplicação
                </p>
              </div>
            </div>

            {!isPublic ? (
              <p className="text-sm text-muted-foreground">
                Ative o link público acima para gerar códigos de incorporação.
              </p>
            ) : !embedMode ? (
              /* Grid de seleção de modo — 3 cards premium */
              <div className="grid grid-cols-3 gap-3">
                {/* Standard */}
                <button
                  onClick={() => setEmbedMode('standard')}
                  className="group flex flex-col items-center gap-3 p-5 rounded-xl border border-border/50 bg-background/50 hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 transition-all duration-200 text-center cursor-pointer"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary/15 transition-colors">
                    <Monitor className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-foreground block">Standard</span>
                    <span className="text-[11px] text-muted-foreground leading-tight">
                      Embed direto em container no seu site
                    </span>
                  </div>
                </button>

                {/* Popup */}
                <button
                  onClick={() => setEmbedMode('popup')}
                  className="group flex flex-col items-center gap-3 p-5 rounded-xl border border-border/50 bg-background/50 hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 transition-all duration-200 text-center cursor-pointer"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary/15 transition-colors">
                    <MessageSquareMore className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-foreground block">Popup</span>
                    <span className="text-[11px] text-muted-foreground leading-tight">
                      Abre em modal overlay sobre a página
                    </span>
                  </div>
                </button>

                {/* Bubble */}
                <button
                  onClick={() => setEmbedMode('bubble')}
                  className="group flex flex-col items-center gap-3 p-5 rounded-xl border border-border/50 bg-background/50 hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 transition-all duration-200 text-center cursor-pointer"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary/15 transition-colors">
                    <MessageCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-foreground block">Bubble</span>
                    <span className="text-[11px] text-muted-foreground leading-tight">
                      Bolha flutuante no canto da tela
                    </span>
                  </div>
                </button>
              </div>
            ) : (
              /* Configuração do modo selecionado */
              <div className="space-y-4">
                {/* Header com voltar */}
                <div className="flex items-center gap-2 pb-2 border-b border-border/30">
                  <Button variant="ghost" size="icon-sm" onClick={() => setEmbedMode(null)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-2">
                    {embedMode === 'standard' && <Monitor className="h-3.5 w-3.5 text-primary" />}
                    {embedMode === 'popup' && <MessageSquareMore className="h-3.5 w-3.5 text-primary" />}
                    {embedMode === 'bubble' && <MessageCircle className="h-3.5 w-3.5 text-primary" />}
                    <span className="text-sm font-semibold text-foreground">
                      {embedMode === 'standard' ? 'Standard' : embedMode === 'popup' ? 'Popup' : 'Bubble'}
                    </span>
                  </div>
                </div>

                {/* Configurações — Standard */}
                {embedMode === 'standard' && (
                  <FormSectionCard title="Configurações da janela" icon={Monitor} compact>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="std-fullscreen" className="text-sm">Tela cheia</Label>
                      <Switch
                        id="std-fullscreen"
                        checked={standardConfig.fullscreen}
                        onCheckedChange={(v) => setStandardConfig((p) => ({ ...p, fullscreen: v }))}
                      />
                    </div>
                    {!standardConfig.fullscreen && (
                      <>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm w-16">Largura</Label>
                          <Input
                            type="number"
                            value={standardConfig.width}
                            onChange={(e) => setStandardConfig((p) => ({ ...p, width: e.target.value }))}
                            className="w-24 h-8"
                          />
                          <Select
                            value={standardConfig.widthUnit}
                            onValueChange={(v: '%' | 'px') => setStandardConfig((p) => ({ ...p, widthUnit: v }))}
                          >
                            <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="%">%</SelectItem>
                              <SelectItem value="px">px</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm w-16">Altura</Label>
                          <Input
                            type="number"
                            value={standardConfig.height}
                            onChange={(e) => setStandardConfig((p) => ({ ...p, height: e.target.value }))}
                            className="w-24 h-8"
                          />
                          <span className="text-sm text-muted-foreground">px</span>
                        </div>
                      </>
                    )}
                  </FormSectionCard>
                )}

                {/* Configurações — Popup */}
                {embedMode === 'popup' && (
                  <FormSectionCard title="Configurações do popup" icon={MessageSquareMore} compact>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="popup-auto" className="text-sm">Auto exibir</Label>
                      <Switch
                        id="popup-auto"
                        checked={popupConfig.autoShow}
                        onCheckedChange={(v) => setPopupConfig((p) => ({ ...p, autoShow: v }))}
                      />
                    </div>
                    {popupConfig.autoShow && (
                      <div className="flex items-center gap-2">
                        <Label className="text-sm w-16">Delay</Label>
                        <Input
                          type="number"
                          min={0}
                          value={popupConfig.delaySeconds}
                          onChange={(e) => setPopupConfig((p) => ({ ...p, delaySeconds: parseInt(e.target.value) || 0 }))}
                          className="w-24 h-8"
                        />
                        <span className="text-sm text-muted-foreground">segundos</span>
                      </div>
                    )}
                  </FormSectionCard>
                )}

                {/* Configurações — Bubble (expandido estilo Typebot) */}
                {embedMode === 'bubble' && (
                  <div className="space-y-3">
                    {/* ---- Seção 1: Chat bubble settings ---- */}
                    <FormSectionCard title="Configurações da bolha" icon={Settings2} compact>
                      {/* Toggle preview message */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor="bubble-preview" className="text-sm">Mensagem de preview</Label>
                        <Switch
                          id="bubble-preview"
                          checked={bubbleConfig.showPreview}
                          onCheckedChange={(v) => setBubbleConfig((p) => ({ ...p, showPreview: v }))}
                        />
                      </div>
                      {bubbleConfig.showPreview && (
                        <div className="space-y-2 pl-0.5">
                          {/* Seleção de avatar via galeria de mídia ou URL manual */}
                          <MediaUrlInput
                            value={bubbleConfig.avatarUrl || null}
                            onChange={(url) => setBubbleConfig((p) => ({ ...p, avatarUrl: url || '' }))}
                            label="Avatar (opcional)"
                            placeholder="https://exemplo.com/avatar.png"
                            showPreview={true}
                            allowedTypes={['image']}
                          />
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Texto da mensagem</Label>
                            <Input
                              value={bubbleConfig.previewMessage}
                              onChange={(e) => setBubbleConfig((p) => ({ ...p, previewMessage: e.target.value }))}
                              className="h-8"
                              placeholder="Olá! Como posso ajudar?"
                            />
                          </div>
                        </div>
                      )}

                      {/* Toggle auto show */}
                      <div className="flex items-center justify-between pt-1 border-t border-border/20">
                        <Label htmlFor="bubble-autoshow" className="text-sm">Auto exibir</Label>
                        <Switch
                          id="bubble-autoshow"
                          checked={bubbleConfig.autoShow}
                          onCheckedChange={(v) => setBubbleConfig((p) => ({ ...p, autoShow: v }))}
                        />
                      </div>
                      {bubbleConfig.autoShow && (
                        <div className="flex items-center gap-2 pl-0.5">
                          <Label className="text-sm w-16">Delay</Label>
                          <Input
                            type="number"
                            min={0}
                            value={bubbleConfig.autoShowDelay}
                            onChange={(e) => setBubbleConfig((p) => ({ ...p, autoShowDelay: parseInt(e.target.value) || 0 }))}
                            className="w-24 h-8"
                          />
                          <span className="text-sm text-muted-foreground">segundos</span>
                        </div>
                      )}
                    </FormSectionCard>

                    {/* ---- Seção 2: Theme ---- */}
                    <FormSectionCard title="Tema" icon={Palette} compact>
                      {/* Posição */}
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Posição</Label>
                        <div className="flex gap-1">
                          <Button
                            variant={bubbleConfig.placement === 'left' ? 'default' : 'outline'}
                            size="xs"
                            onClick={() => setBubbleConfig((p) => ({ ...p, placement: 'left' }))}
                            className="gap-1"
                          >
                            <AlignLeft className="h-3.5 w-3.5" />
                            Esquerda
                          </Button>
                          <Button
                            variant={bubbleConfig.placement === 'right' ? 'default' : 'outline'}
                            size="xs"
                            onClick={() => setBubbleConfig((p) => ({ ...p, placement: 'right' }))}
                            className="gap-1"
                          >
                            <AlignRight className="h-3.5 w-3.5" />
                            Direita
                          </Button>
                        </div>
                      </div>

                      {/* Sub-seção: Botão */}
                      <div className="space-y-2 pt-2 border-t border-border/20">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Botão</span>
                        
                        {/* Tamanho */}
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Tamanho</Label>
                          <Select
                            value={bubbleConfig.buttonSize}
                            onValueChange={(v: 'small' | 'medium' | 'large') => setBubbleConfig((p) => ({ ...p, buttonSize: v }))}
                          >
                            <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="small">Pequeno (48px)</SelectItem>
                              <SelectItem value="medium">Médio (60px)</SelectItem>
                              <SelectItem value="large">Grande (72px)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Cor do botão - paleta + picker */}
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Cor do botão</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1.5">
                              {BUBBLE_COLORS.map((color) => (
                                <button
                                  key={color.value}
                                  onClick={() => setBubbleConfig((p) => ({ ...p, themeColor: color.value }))}
                                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                                    bubbleConfig.themeColor === color.value
                                      ? 'border-foreground scale-110 shadow-md'
                                      : 'border-transparent hover:scale-105'
                                  }`}
                                  style={{ backgroundColor: color.value }}
                                  title={color.label}
                                />
                              ))}
                            </div>
                            <div className="flex items-center gap-1.5 ml-auto">
                              <input
                                type="color"
                                value={bubbleConfig.themeColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, themeColor: e.target.value }))}
                                className="w-8 h-8 rounded-md border border-border cursor-pointer"
                              />
                              <Input
                                value={bubbleConfig.themeColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, themeColor: e.target.value }))}
                                className="w-24 h-8 font-mono text-xs"
                                maxLength={7}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Ícone customizado do botão bubble */}
                        <div className="pt-2">
                          <MediaUrlInput
                            value={bubbleConfig.buttonIconUrl || null}
                            onChange={(url) => setBubbleConfig((p) => ({ ...p, buttonIconUrl: url || '' }))}
                            label="Ícone do botão (opcional)"
                            placeholder="https://exemplo.com/icone.png"
                            showPreview={true}
                            allowedTypes={['image']}
                          />
                        </div>
                      </div>

                      {/* Sub-seção: Preview message colors */}
                      {bubbleConfig.showPreview && (
                        <div className="space-y-2 pt-2 border-t border-border/20">
                          <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Preview message</span>
                          
                          {/* Background color */}
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">Background</Label>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="color"
                                value={bubbleConfig.previewBgColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, previewBgColor: e.target.value }))}
                                className="w-7 h-7 rounded border border-border cursor-pointer"
                              />
                              <Input
                                value={bubbleConfig.previewBgColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, previewBgColor: e.target.value }))}
                                className="w-24 h-8 font-mono text-xs"
                                maxLength={7}
                              />
                            </div>
                          </div>

                          {/* Text color */}
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">Texto</Label>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="color"
                                value={bubbleConfig.previewTextColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, previewTextColor: e.target.value }))}
                                className="w-7 h-7 rounded border border-border cursor-pointer"
                              />
                              <Input
                                value={bubbleConfig.previewTextColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, previewTextColor: e.target.value }))}
                                className="w-24 h-8 font-mono text-xs"
                                maxLength={7}
                              />
                            </div>
                          </div>

                          {/* Close btn bg */}
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">Fechar - fundo</Label>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="color"
                                value={bubbleConfig.closeBtnBgColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, closeBtnBgColor: e.target.value }))}
                                className="w-7 h-7 rounded border border-border cursor-pointer"
                              />
                              <Input
                                value={bubbleConfig.closeBtnBgColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, closeBtnBgColor: e.target.value }))}
                                className="w-24 h-8 font-mono text-xs"
                                maxLength={9}
                              />
                            </div>
                          </div>

                          {/* Close btn icon */}
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">Fechar - ícone</Label>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="color"
                                value={bubbleConfig.closeBtnIconColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, closeBtnIconColor: e.target.value }))}
                                className="w-7 h-7 rounded border border-border cursor-pointer"
                              />
                              <Input
                                value={bubbleConfig.closeBtnIconColor}
                                onChange={(e) => setBubbleConfig((p) => ({ ...p, closeBtnIconColor: e.target.value }))}
                                className="w-24 h-8 font-mono text-xs"
                                maxLength={7}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </FormSectionCard>

                    {/* ---- Seção 3: Preview visual ---- */}
                    <FormSectionCard title="Preview" icon={Eye} compact>
                      <div
                        className="relative bg-background/60 border border-border/30 rounded-lg p-4 h-32 flex items-end"
                        style={{ justifyContent: bubbleConfig.placement === 'left' ? 'flex-start' : 'flex-end' }}
                      >
                        {/* Preview message bubble */}
                        {bubbleConfig.showPreview && (
                          <div
                            className="absolute bottom-[72px] flex items-center gap-2 rounded-lg px-3 py-2 text-xs shadow-sm max-w-[200px]"
                            style={{
                              backgroundColor: bubbleConfig.previewBgColor,
                              color: bubbleConfig.previewTextColor,
                              [bubbleConfig.placement === 'left' ? 'left' : 'right']: '16px',
                            }}
                          >
                            {/* Avatar na preview */}
                            {bubbleConfig.avatarUrl && (
                              <img
                                src={bubbleConfig.avatarUrl}
                                alt="Avatar"
                                className="w-6 h-6 rounded-full flex-shrink-0"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <span>{bubbleConfig.previewMessage || 'Olá!'}</span>
                            {/* Botão fechar na preview */}
                            <div
                              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: bubbleConfig.closeBtnBgColor,
                                color: bubbleConfig.closeBtnIconColor,
                              }}
                            >
                              <X className="w-2.5 h-2.5" />
                            </div>
                          </div>
                        )}
                        {/* Botão bubble */}
                        <div
                          className="rounded-full flex items-center justify-center shadow-lg"
                          style={{
                            backgroundColor: bubbleConfig.themeColor,
                            color: '#fff',
                            width: `${BUBBLE_BUTTON_SIZES[bubbleConfig.buttonSize].size * 0.6}px`,
                            height: `${BUBBLE_BUTTON_SIZES[bubbleConfig.buttonSize].size * 0.6}px`,
                            fontSize: `${BUBBLE_BUTTON_SIZES[bubbleConfig.buttonSize].icon * 0.6}px`,
                          }}
                        >
                          {/* Ícone customizado ou emoji padrão */}
                          {bubbleConfig.buttonIconUrl ? (
                            <img
                              src={bubbleConfig.buttonIconUrl}
                              alt="Ícone do bubble"
                              className="rounded-full object-cover"
                              style={{
                                width: `${BUBBLE_BUTTON_SIZES[bubbleConfig.buttonSize].icon * 0.88}px`,
                                height: `${BUBBLE_BUTTON_SIZES[bubbleConfig.buttonSize].icon * 0.88}px`,
                              }}
                            />
                          ) : (
                            '💬'
                          )}
                        </div>
                      </div>
                    </FormSectionCard>
                  </div>
                )}

                {/* Bloco de código gerado — tema escuro */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                      Código para incorporar
                    </Label>
                    <Button variant="outline" size="xs" onClick={handleCopyEmbedCode} className="gap-1.5">
                      {embedCopied ? (
                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                      {embedCopied ? 'Copiado!' : 'Copiar'}
                    </Button>
                  </div>
                  <pre className="bg-zinc-900 dark:bg-zinc-950 rounded-lg p-4 text-xs overflow-x-auto max-h-64 overflow-y-auto border border-border/20">
                    <code className="text-zinc-300 whitespace-pre-wrap break-all font-mono">
                      {embedCode}
                    </code>
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* ================================================================ */}
          {/* Seção — Rastreamento (GTM, Pixel, GA4, Custom Head) */}
          {/* ================================================================ */}
          <div className={`bg-muted/30 border border-border/40 rounded-xl p-5 space-y-4 transition-opacity ${!isPublic ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Header da seção */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500/10">
                  <BarChart3 className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Rastreamento</h3>
                  <p className="text-xs text-muted-foreground">
                    Configure pixels e tags para rastrear conversões
                  </p>
                </div>
              </div>
            </div>

            {!isPublic ? (
              <p className="text-sm text-muted-foreground">
                Ative o link público acima para configurar rastreamento.
              </p>
            ) : (
              <div className="space-y-4">
                {/* Google Tag Manager ID */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium">Google Tag Manager ID</Label>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[260px]">
                          <p className="text-xs">
                            Insira seu ID do Google Tag Manager (ex: GTM-XXXXXX).
                            O script será injetado automaticamente na página pública do chat.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    value={trackingGtmId}
                    onChange={(e) => setTrackingGtmId(e.target.value)}
                    placeholder="GTM-XXXXXX"
                    className="h-9 font-mono text-sm"
                  />
                </div>

                {/* Meta Pixel ID */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium">Meta Pixel ID</Label>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[260px]">
                          <p className="text-xs">
                            ID do Meta Pixel (Facebook) para rastrear eventos como PageView, Lead e CompleteRegistration.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    value={trackingPixelId}
                    onChange={(e) => setTrackingPixelId(e.target.value)}
                    placeholder="123456789012345"
                    className="h-9 font-mono text-sm"
                  />
                </div>

                {/* Google Analytics 4 */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium">Google Analytics 4</Label>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[260px]">
                          <p className="text-xs">
                            Measurement ID do Google Analytics 4 (ex: G-XXXXXXXXXX) para rastrear eventos e conversões.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    value={trackingGa4Id}
                    onChange={(e) => setTrackingGa4Id(e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                    className="h-9 font-mono text-sm"
                  />
                </div>

                {/* Custom Head Code */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium">Custom head code</Label>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[260px]">
                          <p className="text-xs">
                            Código HTML/JS personalizado que será injetado no {'<head>'} da página pública.
                            Útil para scripts de rastreamento adicionais.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Textarea
                    value={trackingCustomHead}
                    onChange={(e) => setTrackingCustomHead(e.target.value)}
                    placeholder={'<script>\n  // Seu código aqui\n</script>'}
                    className="font-mono text-xs min-h-[100px] resize-y"
                  />
                </div>

                {/* Botão salvar */}
                <div className="flex justify-end pt-1">
                  <Button
                    onClick={() => saveTrackingMutation.mutate()}
                    disabled={saveTrackingMutation.isPending}
                    size="sm"
                    className="gap-1.5"
                  >
                    {saveTrackingMutation.isPending ? (
                      <span className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                    Salvar rastreamento
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}